// frontend/src/utils/leagueColors.ts
//
// League identity accents (owner 2026-07-22). Colors deliberately live
// OUTSIDE the semantic palette - red = Attacking/FW/injury, light blue =
// Creating/MF, green = live/GK - so a league tag can never be misread as a
// stat or status signal:
//   EPL      = violet  (the league's own brand, lightened for the navy bg)
//   La Liga  = coral   (their modern brand's orange end - dodges injury-red)
//   Combined = gold    (the house accent: it's OUR game, not a league)
// Chip volume only - league color goes on tags/pills, never card fills.
// Full literal class strings on purpose: Tailwind's JIT can't see composed
// class names.
import { EPL_COMPETITION_ID, LALIGA_COMPETITION_ID } from '@/utils/featuredContest';

/** Text accent for a contest's league prefix ("EPL", "LA LIGA",
 *  "EPL + LA LIGA"). Empty string = inherit the surrounding title color. */
export const leagueTextClasses = (
  competitions?: { id: number }[] | null
): string => {
  const n = competitions?.length ?? 0;
  if (n > 1) return 'text-accent-yellow';
  const id = competitions?.[0]?.id;
  if (id === EPL_COMPETITION_ID) return 'text-violet-400';
  if (id === LALIGA_COMPETITION_ID) return 'text-orange-400';
  return '';
};

/** Selected-state classes for the lobby's competition filter pills. */
export const leaguePillActiveClasses = (id: number | 'all'): string => {
  if (id === EPL_COMPETITION_ID) return 'bg-violet-400 text-primary shadow-md';
  if (id === LALIGA_COMPETITION_ID) return 'bg-orange-400 text-primary shadow-md';
  return 'bg-accent-yellow text-primary shadow-md';
};

/** Contest-card ring. League identity stays stable across upcoming, entered,
 *  live, and completed states; badges and CTA copy carry semantic status. */
export const leagueCardRingClasses = (
  competitions?: { id: number }[] | null
): string => {
  const n = competitions?.length ?? 0;
  if (n > 1) return 'ring-2 ring-accent-yellow/60';
  const id = competitions?.[0]?.id;
  if (id === EPL_COMPETITION_ID) return 'ring-2 ring-violet-400/60';
  if (id === LALIGA_COMPETITION_ID) return 'ring-2 ring-orange-400/60';
  return 'ring-2 ring-accent-yellow/60';
};

/** Weekend-card top accent border, matching the ring. */
export const leagueTopBorderClasses = (
  competitions?: { id: number }[] | null
): string => {
  const n = competitions?.length ?? 0;
  if (n > 1) return 'border-t-accent-yellow/60';
  const id = competitions?.[0]?.id;
  if (id === EPL_COMPETITION_ID) return 'border-t-violet-400/60';
  if (id === LALIGA_COMPETITION_ID) return 'border-t-orange-400/60';
  return 'border-t-accent-yellow/60';
};

/** Status-badge recipe (bg/20 + text-400 + ring/30, same volume as the
 *  LIVE/ACTIVE/FINAL badges) in the league's color. Used for the UPCOMING
 *  chip, which is league-scoped pre-kickoff; true status badges (live green,
 *  final red) stay status-colored at the call site. */
export const leagueBadgeClasses = (
  competitions?: { id: number }[] | null
): string => {
  const n = competitions?.length ?? 0;
  if (n > 1) return 'bg-accent-yellow/20 text-accent-yellow ring-1 ring-accent-yellow/30';
  const id = competitions?.[0]?.id;
  if (id === EPL_COMPETITION_ID) return 'bg-violet-600/20 text-violet-400 ring-1 ring-violet-500/30';
  if (id === LALIGA_COMPETITION_ID) return 'bg-orange-600/20 text-orange-400 ring-1 ring-orange-500/30';
  return 'bg-accent-yellow/20 text-accent-yellow ring-1 ring-accent-yellow/30';
};

/** CTA button: white text on the league's stronger shade. Combined keeps the
 *  gold/dark-text house button (gold + white text fails contrast). */
export const leagueCtaClasses = (
  competitions?: { id: number }[] | null
): string => {
  const n = competitions?.length ?? 0;
  if (n > 1) return 'bg-accent-yellow text-primary hover:bg-yellow-500 active:bg-yellow-500';
  const id = competitions?.[0]?.id;
  if (id === EPL_COMPETITION_ID) return 'bg-violet-600 text-white hover:bg-violet-500 active:bg-violet-500';
  if (id === LALIGA_COMPETITION_ID) return 'bg-orange-600 text-white hover:bg-orange-500 active:bg-orange-500';
  return 'bg-accent-yellow text-primary hover:bg-yellow-500 active:bg-yellow-500';
};

/** Competition selection cards (create-league wizard, mock draft setup):
 *  selected = league-colored fill, unselected = gray with a league-colored
 *  hover so the card telegraphs its color before you commit.
 *  'combined' = the house gold. */
export const leagueSelectCardClasses = (
  id: number | 'combined',
  selected: boolean
): string => {
  if (id === EPL_COMPETITION_ID) {
    return selected
      ? 'border-violet-400 bg-violet-400 text-primary-dark'
      : 'border-gray-600 bg-primary-dark text-gray-300 hover:border-violet-400 hover:text-violet-400';
  }
  if (id === LALIGA_COMPETITION_ID) {
    return selected
      ? 'border-orange-400 bg-orange-400 text-primary-dark'
      : 'border-gray-600 bg-primary-dark text-gray-300 hover:border-orange-400 hover:text-orange-400';
  }
  // Combined + any future competition without an assigned color
  return selected
    ? 'border-accent-yellow bg-accent-yellow text-primary-dark'
    : 'border-gray-600 bg-primary-dark text-gray-300 hover:border-accent-yellow hover:text-accent-yellow';
};
